import { Link } from 'react-router-dom';

import type { SerializedConfig } from '../lib/api.js';

interface Props {
  config: SerializedConfig;
  onReissue: () => void;
  onDelete: () => void;
  busy?: boolean;
}

export function ConfigCard({ config, onReissue, onDelete, busy }: Props) {
  const revoked = config.status === 'REVOKED';
  const isVless = config.protocol === 'vless';
  // Xray reports no per-user traffic until StatsService is enabled. Showing
  // its zeros as a reading tells the user their config is dead when it is not,
  // so say "unavailable" until the counters mean something.
  const statsAvailable = config.statsAvailable ?? !isVless;
  return (
    <div
      className={`rounded-xl border p-5 ${
        revoked ? 'border-slate-800 bg-slate-900/40 opacity-70' : 'border-slate-800 bg-slate-900/60'
      }`}
    >
      <div className="flex items-start justify-between gap-4">
        <div>
          <Link
            to={`/configs/${config.id}`}
            className="text-lg font-semibold text-slate-100 hover:text-brand"
          >
            {config.name}
          </Link>
          <div className="mt-1 font-mono text-xs text-slate-500">
            {/* VLESS assigns no tunnel address, so the protocol takes its slot. */}
            {config.clientIp ?? 'VLESS'} · {short(config.publicKey)}
          </div>
        </div>
        <span
          className={`rounded-full px-2 py-0.5 text-xs font-medium ${
            revoked
              ? 'bg-slate-800 text-slate-400'
              : 'bg-emerald-500/15 text-emerald-300'
          }`}
        >
          {revoked ? 'отозван' : 'активен'}
        </span>
      </div>

      <dl className="mt-4 grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-slate-400">
        <dt>Создан</dt>
        <dd className="text-right text-slate-300">{formatDate(config.createdAt)}</dd>
        <dt>Последний handshake</dt>
        <dd className="text-right text-slate-300">
          {config.lastHandshakeAt ? formatDate(config.lastHandshakeAt) : '—'}
        </dd>
        <dt>Трафик</dt>
        <dd className="text-right text-slate-300">
          {statsAvailable ? (
            <>
              {formatBytes(config.bytesReceived)} ↓ / {formatBytes(config.bytesSent)} ↑
            </>
          ) : (
            <span className="text-slate-500">статистика недоступна</span>
          )}
        </dd>
      </dl>

      {!revoked && (
        <div className="mt-5 flex items-center gap-3">
          <button
            type="button"
            onClick={onReissue}
            disabled={busy}
            className="rounded-lg border border-slate-700 px-3 py-1.5 text-xs font-medium text-slate-200 hover:border-brand hover:text-brand disabled:opacity-50"
          >
            Перевыпустить
          </button>
          <button
            type="button"
            onClick={onDelete}
            disabled={busy}
            className="rounded-lg border border-red-500/40 px-3 py-1.5 text-xs font-medium text-red-300 hover:bg-red-500/10 disabled:opacity-50"
          >
            Отозвать
          </button>
        </div>
      )}
    </div>
  );
}

function short(key: string): string {
  return `${key.slice(0, 6)}…${key.slice(-4)}`;
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatBytes(value: string): string {
  const n = Number(value);
  if (!Number.isFinite(n) || n === 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let i = 0;
  let v = n;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i++;
  }
  return `${v.toFixed(v >= 100 || i === 0 ? 0 : 1)} ${units[i]}`;
}
